/**
 * @fileoverview Componente responsável por renderizar a versão customizada das ações do ticket.
 */

import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import TriageBadge from "../Triage/TriageBadge";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import useTicketCloseButton from "../../hooks/useTicketCloseButton";
import usePermission from "../../hooks/usePermission";
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import Tooltip from '@material-ui/core/Tooltip';
import { green } from '@material-ui/core/colors';


import { Search, CheckCircle2, MoreVertical, RotateCcw } from "lucide-react";
const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(0.5),
		},
		"& svg": {
			width: 18,
			height: 18,
			strokeWidth: 1.75,
		},
		"& .MuiSvgIcon-root": {
			fontSize: "1.125rem",
		},
	},
}));

const TicketActionButtonsCustom = ({ ticket, onOpenSearch, onTicketUpdate, triageSession, onOpenTriage }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [flowLoading, setFlowLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	const canResolve = useTicketCloseButton();
	// Retomar o fluxo pausado devolve a conversa ao usuário IA do fluxo: é
	// transferir (hoje aberto a todos; com a regra nova, tickets.transfer).
	// Pausar fica com quem atende.
	const { can } = usePermission();
	const canResumeFlow = can("tickets.transfer", true);

	const customTheme = createTheme({
		palette: {
		  	primary: green,
		}
	});

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleToggleFlow = async () => {
		setFlowLoading(true);
		try {
			const newValue = !ticket.flowPaused;
			const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, {
				flowPaused: newValue,
			});
			if (onTicketUpdate) {
				onTicketUpdate(updatedTicket);
			}
		} catch (err) {
			toastError(err);
		}
		setFlowLoading(false);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? false : ticket.promptId,
				integrationId: status === "closed" ? false : ticket.integrationId
			});

			setLoading(false);
			if (status === "open") {
				// Atualizar o ticket no componente pai com os dados da resposta da API
				if (onTicketUpdate) {
					onTicketUpdate(updatedTicket);
				}
				setCurrentTicket({ ...updatedTicket, code: "#open" });
				// Navegar para o ticket reaberto na aba Atendendo
				history.push(`/tickets/${updatedTicket.uuid}`);
			} else {
				setCurrentTicket({ id: null, code: null })
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons}>
			{/* Triagem em andamento: selo com o progresso, abre a aba Triagem */}
			<TriageBadge session={triageSession} onClick={onOpenTriage} />

			{/* Botão de busca sempre visível */}
			<Tooltip title="Pesquisar mensagens">
				<IconButton onClick={onOpenSearch} size="small">
					<Search />
				</IconButton>
			</Tooltip>

			{/* Botão pausar/ativar fluxo — só aparece se ticket tem workflow ativo.
			    Com triagem em andamento, pausar e entregar à IA ficam no painel dela. */}
			{ticket.status === "open" && ticket.hasActiveWorkflow && !triageSession && (
				<Tooltip
					title={
						ticket.flowPaused
							? canResumeFlow ? "Ativar fluxo IA" : "Fluxo IA pausado"
							: "Pausar fluxo IA"
					}
				>
					<span>
						<IconButton
							onClick={handleToggleFlow}
							size="small"
							disabled={flowLoading || (ticket.flowPaused && !canResumeFlow)}
							style={{ color: ticket.flowPaused ? "#f59e0b" : "#8b5cf6" }}
						>
							{ticket.flowPaused ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />}
						</IconButton>
					</span>
				</Tooltip>
			)}

			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<RotateCcw />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					{/* Botão Retornar removido */}
					{/* <Tooltip title={i18n.t("messagesList.header.buttons.return")}>
						<IconButton onClick={e => handleUpdateTicketStatus(e, "pending", null)}>
							<UndoRoundedIcon />
						</IconButton>
					</Tooltip> */}
					{/* Sem o botão quando a empresa tirou do atendente o poder de encerrar. */}
					{canResolve && (
						<ThemeProvider theme={customTheme}>
							<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
								<span>
									<IconButton onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)} color="primary">
										<CheckCircle2 />
									</IconButton>
								</span>
							</Tooltip>
						</ThemeProvider>
					)}
					{/* <ButtonWithSpinner
						loading={loading}
						startIcon={<RotateCcw />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner> */}
					{/* Menu de 3 pontinhos removido - agora as opções estão na setinha do ticket list */}
					{/* <IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVertical />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/> */}
				</>
			)}
			{ticket.status === "pending" && (
				<Tooltip title={!ticket.whatsappId ? "Associe uma conexão WhatsApp para aceitar" : ""}>
					<span>
						<ButtonWithSpinner
							loading={loading}
							size="small"
							variant="contained"
							color="primary"
							disabled={!ticket.whatsappId}
							onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
						>
							{i18n.t("messagesList.header.buttons.accept")}
						</ButtonWithSpinner>
					</span>
				</Tooltip>
			)}
		</div>
	);
};

export default TicketActionButtonsCustom;
